import { useEffect, useMemo, useRef, useState } from "react";
import { fetchListadoJugadores } from "./useJugadoresApi";

import { imageUrl } from '../../../config/api';
const PAGE_SIZE = 8;
const VIEW_STATE_KEY = "jugadores:viewState:v1";

function readStoredViewState() {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const raw = window.sessionStorage.getItem(VIEW_STATE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_error) {
        return null;
    }
}

function writeStoredViewState(payload) {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify(payload));
    } catch (_error) {
        // Ignore sessionStorage write errors (private mode/quota)
    }
}

function normalizeText(value) {
    return String(value || "")
        .toLocaleLowerCase("es")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}

function normalizeNumber(value) {
    const onlyDigits = String(value || "").replace(/\D/g, "");

    if (!onlyDigits) {
        return "";
    }

    return String(Number(onlyDigits));
}

function imageUrlFor(nombre) {
    return imageUrl(nombre);
}

export default function useJugadoresData() {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [searchTerm, setSearchTerm] = useState(() => readStoredViewState()?.searchTerm ?? "");
    const [jugadores, setJugadores] = useState([]);
    const [currentPage, setCurrentPage] = useState(() => {
        const stored = readStoredViewState()?.currentPage;
        return Number.isInteger(stored) && stored > 0 ? stored : 1;
    });
    const isFirstSearchRender = useRef(true);

    useEffect(() => {
        let isMounted = true;

        async function loadJugadores() {
            setIsError(false);

            try {
                const listado = await fetchListadoJugadores();

                if (!isMounted) {
                    return;
                }

                setJugadores(
                    listado.map((jugador) => ({
                        ...jugador,
                        posicion: "Jugador",
                        imageUrl: imageUrlFor(jugador.nombre),
                    }))
                );
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                setIsError(true);
                setJugadores([]);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadJugadores();

        return () => {
            isMounted = false;
        };
    }, []);

    const normalizedSearch = useMemo(() => normalizeText(searchTerm).trim(), [searchTerm]);
    const isExactNumberSearch = useMemo(() => /^\d+$/.test(normalizedSearch), [normalizedSearch]);
    const normalizedNumberSearch = useMemo(() => normalizeNumber(normalizedSearch), [normalizedSearch]);

    const filteredJugadores = useMemo(() => {
        const normalizedSearchValue = normalizedSearch;

        if (!normalizedSearchValue) {
            return jugadores;
        }

        return jugadores.filter((jugador) => {
            const nombre = normalizeText(jugador.nombre);
            const nombreMatches = nombre.includes(normalizedSearchValue);
            const nacionalidad = normalizeText(jugador.nacionalidad);
            const nacionalidadMatches = nacionalidad.includes(normalizedSearchValue);

            if (nombreMatches || nacionalidadMatches) {
                return true;
            }

            if (!isExactNumberSearch) {
                return false;
            }

            const numeros = Array.isArray(jugador.numeros) ? jugador.numeros : [];
            return numeros.some((numero) => normalizeNumber(numero) === normalizedNumberSearch);
        });
    }, [jugadores, normalizedSearch, isExactNumberSearch, normalizedNumberSearch]);

    const totalPages = Math.max(1, Math.ceil(filteredJugadores.length / PAGE_SIZE));

    const safeCurrentPage = Math.min(currentPage, totalPages);

    const paginatedBaseJugadores = useMemo(() => {
        const start = (safeCurrentPage - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        return filteredJugadores.slice(start, end);
    }, [filteredJugadores, safeCurrentPage]);

    const paginatedJugadores = paginatedBaseJugadores;

    useEffect(() => {
        const imageUrls = paginatedJugadores
            .map((jugador) => jugador?.imageUrl)
            .filter(Boolean);

        const preloadImages = imageUrls.map((url) => {
            const img = new Image();
            img.src = url;
            return img;
        });

        return () => {
            preloadImages.forEach((img) => {
                img.src = "";
            });
        };
    }, [paginatedJugadores]);

    useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false;
            return;
        }
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        if (jugadores.length === 0) {
            return;
        }
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages, jugadores.length]);

    useEffect(() => {
        writeStoredViewState({ searchTerm, currentPage });
    }, [searchTerm, currentPage]);

    const pageNumbers = useMemo(() => {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }, [totalPages]);

    return {
        isLoading,
        isError,
        hasData: jugadores.length > 0,
        searchTerm,
        setSearchTerm,
        jugadores: paginatedJugadores,
        totalJugadores: jugadores.length,
        filteredTotal: filteredJugadores.length,
        currentPage: safeCurrentPage,
        totalPages,
        pageNumbers,
        onPageChange: setCurrentPage,
    };
}
